import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TransactionGatewayTracking = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [gatewayFilter, setGatewayFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [processingStageFilter, setProcessingStageFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [gatewayMetrics, setGatewayMetrics] = useState([
    {
      gateway: 'Stripe',
      status: 'Online',
      successRate: 98.5,
      avgProcessingTime: '1.2s',
      failureRate: 1.5,
      uptime: 99.9,
      todayTransactions: 1247,
      todayVolume: 4567890
    },
    {
      gateway: 'PayPal',
      status: 'Online',
      successRate: 97.2,
      avgProcessingTime: '2.1s',
      failureRate: 2.8,
      uptime: 99.5,
      todayTransactions: 856,
      todayVolume: 2345678
    },
    {
      gateway: 'Razorpay',
      status: 'Online',
      successRate: 96.8,
      avgProcessingTime: '1.8s',
      failureRate: 3.2,
      uptime: 99.2,
      todayTransactions: 543,
      todayVolume: 1234567
    }
  ]);

  const [transactions, setTransactions] = useState([
    {
      id: 1,
      transactionId: 'TXN-2026-001234',
      gateway: 'Stripe',
      amount: 14999,
      status: 'Completed',
      processingStage: 'Settlement',
      timestamp: '2026-02-08 16:45:23',
      stages: [
        { name: 'Authorization', status: 'completed', timestamp: '2026-02-08 16:45:23', duration: '0.5s' },
        { name: 'Capture', status: 'completed', timestamp: '2026-02-08 16:45:24', duration: '0.3s' },
        { name: 'Settlement', status: 'completed', timestamp: '2026-02-08 16:45:25', duration: '0.4s' }
      ],
      gatewayRequest: '{"amount": 14999, "currency": "INR"}',
      gatewayResponse: '{"status": "success", "transaction_id": "ch_1234567890"}',
      errorCode: null,
      retryAttempts: 0
    },
    {
      id: 2,
      transactionId: 'TXN-2026-001235',
      gateway: 'PayPal',
      amount: 5999,
      status: 'Processing',
      processingStage: 'Capture',
      timestamp: '2026-02-08 16:44:15',
      stages: [
        { name: 'Authorization', status: 'completed', timestamp: '2026-02-08 16:44:15', duration: '0.8s' },
        { name: 'Capture', status: 'in_progress', timestamp: '2026-02-08 16:44:16', duration: '-' },
        { name: 'Settlement', status: 'pending', timestamp: null, duration: '-' }
      ],
      gatewayRequest: '{"amount": 5999, "currency": "INR"}',
      gatewayResponse: '{"status": "pending"}',
      errorCode: null,
      retryAttempts: 0
    },
    {
      id: 3,
      transactionId: 'TXN-2026-001236',
      gateway: 'Stripe',
      amount: 2999,
      status: 'Failed',
      processingStage: 'Authorization',
      timestamp: '2026-02-08 16:43:10',
      stages: [
        { name: 'Authorization', status: 'failed', timestamp: '2026-02-08 16:43:10', duration: '0.5s' },
        { name: 'Capture', status: 'not_started', timestamp: null, duration: '-' },
        { name: 'Settlement', status: 'not_started', timestamp: null, duration: '-' }
      ],
      gatewayRequest: '{"amount": 2999, "currency": "INR"}',
      gatewayResponse: '{"status": "failed", "error": "insufficient_funds"}',
      errorCode: 'INSUFFICIENT_FUNDS',
      retryAttempts: 2
    },
    {
      id: 4,
      transactionId: 'TXN-2026-001237',
      gateway: 'Razorpay',
      amount: 2999,
      status: 'Processing',
      processingStage: 'Authorization',
      timestamp: '2026-02-08 16:42:05',
      stages: [
        { name: 'Authorization', status: 'in_progress', timestamp: '2026-02-08 16:42:05', duration: '-' },
        { name: 'Capture', status: 'pending', timestamp: null, duration: '-' },
        { name: 'Settlement', status: 'pending', timestamp: null, duration: '-' }
      ],
      gatewayRequest: '{"amount": 2999, "currency": "INR"}',
      gatewayResponse: null,
      errorCode: null,
      retryAttempts: 0
    }
  ]);

  const [alerts, setAlerts] = useState([
    {
      id: 1,
      type: 'warning',
      gateway: 'PayPal',
      message: 'Processing time increased by 15% in last hour',
      timestamp: '2026-02-08 16:30:00'
    },
    {
      id: 2,
      type: 'info',
      gateway: 'Stripe',
      message: 'High transaction volume detected',
      timestamp: '2026-02-08 16:15:00'
    }
  ]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        console.log('Auto-refreshing transaction data...');
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const handleExportTechnicalAnalysis = () => {
    console.log('Exporting technical analysis...');
  };

  const handleViewTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
  };

  const filteredTransactions = transactions?.filter(txn => {
    const matchesSearch = txn?.transactionId?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesGateway = gatewayFilter === 'all' || txn?.gateway === gatewayFilter;
    const matchesStatus = statusFilter === 'all' || txn?.status === statusFilter;
    const matchesStage = processingStageFilter === 'all' || txn?.processingStage === processingStageFilter;
    return matchesSearch && matchesGateway && matchesStatus && matchesStage;
  });

  const totalPages = Math.ceil(filteredTransactions?.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (status) => {
    const config = {
      'Completed': { bg: 'bg-success/10', text: 'text-success', icon: 'CheckCircle' },
      'Processing': { bg: 'bg-warning/10', text: 'text-warning', icon: 'Clock' },
      'Failed': { bg: 'bg-destructive/10', text: 'text-destructive', icon: 'XCircle' }
    };
    const statusConfig = config?.[status] || config?.['Processing'];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.bg} ${statusConfig?.text}`}>
        <Icon name={statusConfig?.icon} size={12} />
        {statusConfig?.label || status}
      </span>
    );
  };

  const getStageStatusIcon = (status) => {
    const config = {
      'completed': { icon: 'CheckCircle', color: 'text-success' },
      'in_progress': { icon: 'Clock', color: 'text-warning' },
      'failed': { icon: 'XCircle', color: 'text-destructive' },
      'pending': { icon: 'Circle', color: 'text-muted-foreground' },
      'not_started': { icon: 'Circle', color: 'text-muted-foreground' }
    };
    const stageConfig = config?.[status] || config?.['pending'];
    return <Icon name={stageConfig?.icon} size={16} className={stageConfig?.color} />;
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar isCollapsed={sidebarCollapsed} />
      <div className={`flex-1 transition-smooth ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading flex items-center gap-3">
                  <Icon name="ArrowLeftRight" size={32} className="text-primary" />
                  Transaction Gateway Tracking
                </h1>
                <p className="text-sm md:text-base text-muted-foreground font-body mt-2">
                  Real-time payment processing monitoring and transaction lifecycle oversight
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-smooth ${autoRefresh ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}
                >
                  <Icon name={autoRefresh ? 'RefreshCw' : 'Pause'} size={16} className={autoRefresh ? 'animate-spin' : ''} />
                  {autoRefresh ? 'Auto-Refresh On' : 'Auto-Refresh Off'}
                </button>
              </div>
            </div>
          </div>

          {/* Gateway Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {gatewayMetrics?.map((metric) => (
              <div key={metric?.gateway} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon name="CreditCard" size={20} className="text-primary" />
                    <h3 className="font-semibold text-foreground">{metric?.gateway}</h3>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${metric?.status === 'Online' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
                    <span className={`w-2 h-2 rounded-full ${metric?.status === 'Online' ? 'bg-success' : 'bg-destructive'}`}></span>
                    {metric?.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Success Rate</p>
                    <p className="text-lg font-semibold text-success">{metric?.successRate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg Time</p>
                    <p className="text-lg font-semibold text-foreground">{metric?.avgProcessingTime}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Uptime</p>
                    <p className="text-lg font-semibold text-foreground">{metric?.uptime}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Today's TXN</p>
                    <p className="text-lg font-semibold text-foreground">{metric?.todayTransactions}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">Today's Volume</p>
                  <p className="text-sm font-semibold text-foreground">₹{metric?.todayVolume?.toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Alerts */}
          {alerts?.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-foreground flex items-center gap-2 mb-3">
                <Icon name="AlertTriangle" size={18} className="text-warning" />
                System Alerts
              </h3>
              <div className="space-y-2">
                {alerts?.map((alert) => (
                  <div key={alert?.id} className={`flex items-start gap-3 p-3 rounded-md ${alert?.type === 'warning' ? 'bg-warning/10' : 'bg-info/10'}`}>
                    <Icon name={alert?.type === 'warning' ? 'AlertTriangle' : 'Info'} size={16} className={alert?.type === 'warning' ? 'text-warning' : 'text-info'} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{alert?.gateway}: {alert?.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">{alert?.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Icon name="Filter" size={20} />
                Transaction Filters
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportTechnicalAnalysis}
                iconName="Download"
                iconPosition="left"
              >
                Export Analysis
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search transaction ID..."
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <select
                value={gatewayFilter}
                onChange={(e) => setGatewayFilter(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="all">All Gateways</option>
                <option value="Stripe">Stripe</option>
                <option value="PayPal">PayPal</option>
                <option value="Razorpay">Razorpay</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="all">All Status</option>
                <option value="Completed">Completed</option>
                <option value="Processing">Processing</option>
                <option value="Failed">Failed</option>
              </select>
              <select
                value={processingStageFilter}
                onChange={(e) => setProcessingStageFilter(e?.target?.value)}
                className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="all">All Stages</option>
                <option value="Authorization">Authorization</option>
                <option value="Capture">Capture</option>
                <option value="Settlement">Settlement</option>
              </select>
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Transaction ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Gateway</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Stage</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions?.map((txn) => (
                  <React.Fragment key={txn?.id}>
                    <tr className="border-b border-border hover:bg-muted/30 transition-smooth">
                      <td className="px-4 py-4 font-mono text-sm text-foreground">{txn?.transactionId}</td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center gap-2 px-2 py-1 bg-muted rounded-md text-sm font-medium text-foreground">
                          <Icon name="CreditCard" size={14} />
                          {txn?.gateway}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-foreground font-semibold">₹{txn?.amount?.toLocaleString()}</td>
                      <td className="px-4 py-4">{getStatusBadge(txn?.status)}</td>
                      <td className="px-4 py-4 text-foreground">{txn?.processingStage}</td>
                      <td className="px-4 py-4 text-foreground text-sm">{txn?.timestamp}</td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => handleViewTransactionDetails(txn)}
                          className="p-2 hover:bg-muted rounded-md transition-smooth"
                          title="View Details"
                        >
                          <Icon name="Eye" size={16} className="text-primary" />
                        </button>
                      </td>
                    </tr>
                    {selectedTransaction?.id === txn?.id && (
                      <tr className="bg-muted/20 border-b border-border">
                        <td colSpan="7" className="px-4 py-4">
                          <div className="space-y-4">
                            {/* Processing Stages */}
                            <div>
                              <h4 className="text-sm font-semibold text-foreground mb-3">Processing Stages</h4>
                              <div className="flex items-center gap-4">
                                {txn?.stages?.map((stage, index) => (
                                  <React.Fragment key={stage?.name}>
                                    <div className="flex flex-col items-center">
                                      {getStageStatusIcon(stage?.status)}
                                      <p className="text-xs font-medium text-foreground mt-1">{stage?.name}</p>
                                      <p className="text-xs text-muted-foreground">{stage?.duration}</p>
                                      {stage?.timestamp && (
                                        <p className="text-xs text-muted-foreground">{stage?.timestamp}</p>
                                      )}
                                    </div>
                                    {index < txn?.stages?.length - 1 && (
                                      <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                                    )}
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>
                            {/* Technical Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-sm font-semibold text-foreground mb-2">Gateway Request</h4>
                                <pre className="bg-background border border-border rounded p-2 text-xs text-foreground overflow-x-auto">{txn?.gatewayRequest}</pre>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-foreground mb-2">Gateway Response</h4>
                                <pre className="bg-background border border-border rounded p-2 text-xs text-foreground overflow-x-auto">{txn?.gatewayResponse || 'Awaiting response...'}</pre>
                              </div>
                            </div>
                            {txn?.errorCode && (
                              <div>
                                <h4 className="text-sm font-semibold text-destructive mb-2">Error Details</h4>
                                <p className="text-sm text-foreground">Error Code: <span className="font-mono text-destructive">{txn?.errorCode}</span></p>
                                <p className="text-sm text-foreground">Retry Attempts: {txn?.retryAttempts}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4 mb-6">
            {paginatedTransactions?.map((txn) => (
              <div key={txn?.id} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono text-sm font-semibold text-foreground">{txn?.transactionId}</p>
                    <p className="text-xs text-muted-foreground mt-1">{txn?.timestamp}</p>
                  </div>
                  {getStatusBadge(txn?.status)}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Gateway</p>
                    <p className="text-sm font-medium text-foreground">{txn?.gateway}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Amount</p>
                    <p className="text-sm font-semibold text-foreground">₹{txn?.amount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Stage</p>
                    <p className="text-sm font-medium text-foreground">{txn?.processingStage}</p>
                  </div>
                  {txn?.errorCode && (
                    <div>
                      <p className="text-xs text-muted-foreground">Error</p>
                      <p className="text-sm font-mono text-destructive">{txn?.errorCode}</p>
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewTransactionDetails(txn?.id === selectedTransaction?.id ? null : txn)}
                  iconName="Eye"
                  iconPosition="left"
                  fullWidth
                >
                  {txn?.id === selectedTransaction?.id ? 'Hide Details' : 'View Details'}
                </Button>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredTransactions?.length)} of {filteredTransactions?.length} transactions
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  iconName="ChevronLeft"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  iconName="ChevronRight"
                  iconPosition="right"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionGatewayTracking;